import { clsx } from 'clsx';
import { createUseStyles } from 'react-jss';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';

import { Typography } from '../../Typography';
import type { LcpnTheme } from '../../../styles/theme';
import { hexToRgb } from '../../../utils/colour-utils';
import { countryStatusConfig } from '../../../map-data/heatmap-legend-config';

const useStyles = createUseStyles((theme: LcpnTheme) => ({
  wrapper: {
    display: 'block',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: theme.colors.white,
    borderTop: `${theme.colors['grey-100']} solid 2px`,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },

  title: {
    marginLeft: `clamp(${theme.spacing(8)}, 5.5%, ${theme.spacing(8)})`,
    color: theme.colors['green-100'],
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(6),
  },

  group: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    maxWidth: 600,
    borderBottom: `${theme.colors['green-10']} solid 2px`,
    paddingRight: '5em',
  },

  status: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(4),
    marginLeft: `clamp(${theme.spacing(8)}, 5.5%, ${theme.spacing(8)})`,
    boxSizing: 'border-box',
    paddingBottom: theme.spacing(4),
    borderBottomWidth: '4px',
    borderBottomColor: theme.colors.white,
    borderBottomStyle: 'solid',
  },

  footnote: {
    marginLeft: `clamp(${theme.spacing(8)}, 5.5%, ${theme.spacing(8)})`,
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },

  active: {},

  [`@media screen and (max-width: ${theme.breakPoints.tablet}px)`]: {
    status: {
      '&&': {
        marginLeft: theme.spacing(2),
      },
    },
    title: {
      '&&': {
        marginLeft: theme.spacing(2),
      },
    },
  },
}));

interface StatusBarProps {
  country: string;
  status: string;
  footnote?: string;
}

export const StatusBar: FC<StatusBarProps> = ({
  country,
  status,
  footnote,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const statuses = countryStatusConfig
    .filter(s => s.active)
    .map(s => ({
      key: s.dataValue,
      title: t(`countryStatus.${s.translationKey}`),
      color: hexToRgb(s.color),
    }));

  return (
    <div className={classes.wrapper}>
      <div className={classes.title}>
        <Typography variant="p3">
          {t('countryRow.statusBar.stage', { replace: { country } })}
        </Typography>
      </div>
      <div className={classes.group}>
        {statuses.map((value, i) => (
          <div
            className={clsx(classes.status, {
              [classes.active]: status === value.key,
            })}
            style={{
              borderBottomColor:
                status === value.key
                  ? 'rgba(' + value.color + ')'
                  : 'transparent',
            }}
            key={`status` + i}
          >
            <Typography variant="p3">{value.title}</Typography>
          </div>
        ))}
      </div>
      {footnote && (
        <div className={classes.footnote}>
          <Typography variant="p3">{footnote}</Typography>
        </div>
      )}
    </div>
  );
};
